import { useRef, useEffect, useCallback, Component } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { EChartsOption, ECharts as EChartsInstance } from 'echarts';
import echarts, { patchZrHandlerForStaleSeriesCrash } from '@/lib/echarts';
import { ECHARTS_THEME } from '@/lib/echarts-theme';

const THEME_NAME = 'app-theme';
let themeRegistered = false;

function ensureTheme() {
  if (!themeRegistered) {
    echarts.registerTheme(THEME_NAME, ECHARTS_THEME);
    themeRegistered = true;
  }
}

interface EChartProps {
  option: EChartsOption;
  height?: number | string;
  className?: string;
  notMerge?: boolean;
  lazyUpdate?: boolean;
  onEvents?: Record<string, (params: any) => void>;
}

class EChartErrorBoundary extends Component<{ height: number | string; children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      const h = typeof this.props.height === 'number' ? `${this.props.height}px` : this.props.height;
      return (
        <div style={{ height: h, width: '100%' }} className="flex items-center justify-center text-muted-foreground text-sm">
          Chart unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

// ECharts has a known race condition where, when `setOption` swaps in new
// series data while the cursor is hovering a data point, the next `mouseout`
// dispatch reads `getRawIndex` on the stale (now-undefined) series data and
// throws "Cannot read properties of undefined (reading 'getRawIndex')".
//
// Dispatching `hideTip` + `downplay` immediately before every option update
// clears the hovered element ref, making the subsequent mouseout a no-op.
// We patch the instance once it's ready so the protection applies to every
// internal setOption call from `echarts-for-react`.
//
// See: https://github.com/apache/echarts/issues/19191
function patchSetOptionToHideTip(instance: EChartsInstance) {
  const patched = instance as EChartsInstance & { __hideTipPatched?: boolean };
  if (patched.__hideTipPatched) return;
  patched.__hideTipPatched = true;

  const originalSetOption = instance.setOption.bind(instance);
  instance.setOption = function patchedSetOption(...args: Parameters<typeof originalSetOption>) {
    if (!instance.isDisposed()) {
      try {
        instance.dispatchAction({ type: 'hideTip' });
        instance.dispatchAction({ type: 'downplay' });
      } catch {
        // Defensive: never let the safety dispatch break the real update.
      }
    }
    return originalSetOption(...args);
  } as typeof originalSetOption;
}

// Second-line defense for the same ECharts bug. The primary guard patches the
// zrender `Handler` prototype once at module load (see `@/lib/echarts`). As a
// safety net against any module-dedup mismatch where the runtime handler class
// differs from the one imported there, we re-run the same idempotent prototype
// patch against the live instance's handler class the first time a chart is
// ready. It's a no-op when the prototype is already patched.
function patchRuntimeZrHandler(instance: EChartsInstance) {
  const zr = (instance as unknown as { getZr?: () => any }).getZr?.();
  const handler = zr?.handler;
  if (!handler) return;
  patchZrHandlerForStaleSeriesCrash(handler.constructor);
}

function EChartInner({
  option,
  height = 280,
  className,
  notMerge = true,
  lazyUpdate = true,
  onEvents,
}: EChartProps) {
  ensureTheme();
  const chartRef = useRef<ReactEChartsCore>(null);

  const handleChartReady = useCallback((instance: EChartsInstance) => {
    patchSetOptionToHideTip(instance);
    patchRuntimeZrHandler(instance);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const instance = chartRef.current?.getEchartsInstance();
      if (instance && !instance.isDisposed()) {
        instance.resize();
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={option}
      theme={THEME_NAME}
      notMerge={notMerge}
      lazyUpdate={lazyUpdate}
      style={{ height: typeof height === 'number' ? `${height}px` : height, width: '100%' }}
      className={className}
      opts={{ renderer: 'canvas' }}
      onEvents={onEvents}
      onChartReady={handleChartReady}
    />
  );
}

export function EChart(props: EChartProps) {
  return (
    <EChartErrorBoundary height={props.height ?? 280}>
      <EChartInner {...props} />
    </EChartErrorBoundary>
  );
}
